import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { format, isValid, parse } from 'date-fns';
import { default as IMask, default as Opts } from 'imask'; //only import whats neccessary here
import sanitizeHtml from 'sanitize-html';
import {
  applySchemeDelegate,
  ColorableComponent,
  getColorOfNearestBackground,
  setCssVariablesDelegate,
} from '../../common/colorable-component';
import {
  FormComponent,
  handleCustomValidationDelegate,
  renderRelevantNecessity,
  triggerValidationDelegate,
  validateDelegate,
  validateRequiredValueDelegate,
} from '../../common/form-component';
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from '../../common/localizable-component';
import { InputSchemeConfiguration } from '../../constants/colors';
import { KeyIds } from '../../constants/keys';
import { StyledComponent } from '../../decorators/styled-component';
import { setCssVariable } from '../../utils/css-variables';
import { isFirefox, isSafari, isSmartDevice } from '../../utils/device-helper';
import { allowClassesAndLinks } from '../../utils/sanitize-helper';
import pfx from '../../utils/style-class-prefix-helper';
import { isTouchDevice } from '../../utils/touch-helper';
import { uniqueId } from '../../utils/unique-id';
import { BackgroundColor } from '../background/constants/background-types';
import { FORM_FIELD_NECESSITY_NONE } from '../form/constants/form-constants';
import { FormFieldNecessity } from '../form/constants/form-types';
import { PRELOADER_CIRCLE_SIZE_SMALL } from '../preloader-circle/constants/preloader-circle-constants';
import {
  INPUT_CURRENCIES,
  INPUT_MODE_OPTIONS,
  INPUT_PASSWORD,
  INPUT_SCHEME_CONFIG,
  INPUT_SCHEME_MAP,
  INPUT_TEXT,
  NUMBER_REGEX1,
  NUMBER_REGEX2,
} from './constants/input-constants';
import { INPUT_LOCALIZATION } from './constants/input-i18n';
import {
  InputCurrencies,
  InputLocalization,
  InputModeOptions,
  InputSchemeDefinition,
  InputSchemeName,
  InputType,
  SelectedDatePart,
} from './constants/input-types';

// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot info-icon - Holds a single envc-eon-ui-info-icon element
 */
@Component({
  tag: 'envc-eon-ui-input',
  styleUrl: 'input.scss',
  shadow: true,
  formAssociated: true,
})
export class Input
  implements
    ColorableComponent<InputSchemeConfiguration<InputSchemeDefinition>>,
    LocalizableComponent<InputLocalization>,
    FormComponent<any, InputLocalization>
{
  @AttachInternals() internals: ElementInternals;
  // @InitLogging() sentryTransaction;
  schemeConfig: InputSchemeConfiguration<InputSchemeDefinition> = INPUT_SCHEME_CONFIG;
  schemeMap = INPUT_SCHEME_MAP;

  localization: Localization<InputLocalization> = INPUT_LOCALIZATION;
  currentLocalization: InputLocalization;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiInputElement;

  private input?: HTMLInputElement | HTMLTextAreaElement;
  private internalDateMask: IMask.InputMask<Opts.MaskedDateOptions>;
  private internalPatternMask: IMask.InputMask<Opts.MaskedPatternOptions>;
  private internalNumberMask: IMask.InputMask<Opts.MaskedNumberOptions>;
  private valueChangedEventEmitted: boolean = false;
  private tempValueForBlurComparison: string;
  private internalId: string;

  @State() activeScheme: InputSchemeDefinition;

  @State() labelOffset: number = 0;
  @State() inputOffset: number = 0;
  @State() activeLabelOffset: number = 0;
  @State() clearOffset: number = 0;
  /**
   fieldSpaceCalculation
   */
  @State() paddingLeft: number = 0;
  @State() infoIconMargin: number = 0;

  private navigationElementsContainer: HTMLElement;
  private hideAutocomplete = false;

  /**
   * Whether or not the Dropdownlist is collapsed default is set to true.
   */
  @State() collapsed: boolean = true;

  /**
   * Fires when the user selects an item in the autocomplete list
   */
  @Event() autocompleteSelected: EventEmitter<string>;

  /**
   * Used for the password toggle, gets set while the component is loaded
   */
  @State() internalType: InputType;

  /**
   * Used to store the results of autocomplete request
   */
  @State() results: Array<any>;

  /**
   * Whether or not an existing CustomValidator has and error.
   */
  @State() customValidatorHasError: boolean = false;

  @State() isFocus: boolean;

  @State() passwordVisible: boolean = false;

  @State() isInfoIconAvailable: boolean;

  @State() masked: boolean = false;

  @State() componentRendered: boolean = false;

  /**
   * State for which part of the date is currently selected
   */
  @State() selectedDatePart: SelectedDatePart = '';

  /**
   * State for which inputmode should be used, default is "text"
   */
  @State() finalInternalInputMode: string = INPUT_MODE_OPTIONS[1];

  /**
   * Raised when the input has changed. Will emit the current value of the Input field.
   */
  @Event({ composed: false }) valueChanged: EventEmitter<any>;

  /**
   * Raised when the input has changed programmatically. Will emit the current value of the Input field.
   */
  @Event({ composed: false }) valueChangedProgrammatically: EventEmitter<any>;

  /**
   * Raised when the input is focused.
   */
  @Event() inputFocus: EventEmitter<FocusEvent>;

  /**
   * Raised when the input is losing focus.
   */
  @Event() inputBlur: EventEmitter<FocusEvent>;

  /**Raised when the trailing icon is clicked. */
  @Event() iconClick: EventEmitter<MouseEvent>;

  /**
   * Show or hide preloader
   */
  @Prop({ mutable: true }) preloader: boolean = false;

  /**
   * Show or hide preloader
   */
  @Prop() openAutocompleteOnFocus: boolean = true;

  /**
   * Internal use only! Please don't change this.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true }) isFormComponent: boolean = true;

  /**
   * Define the size of the input element
   */
  @Prop() size: 'small' | 'normal' = 'normal';

  /**
   * The name of the input must be unique per page.!
   *@example <envc-eon-ui-input name="firstname"></envc-eon-ui-input>
   */
  @Prop() name: string = '';

  /**
   * The value of the input element.
   * @example <envc-eon-ui-input value="Test"></envc-eon-ui-input>
   */
  @Prop({ reflect: true, mutable: true }) value: string = '';
  /**
   * Wir müssen wissen, ob der Fokus programmatisch gesetzt wurde durch die explizite Tastatursteuerung.
   * Das Attribut wird dann schließlich vom Konsumenten wieder zurückgesetzt.
   */
  @Prop({ reflect: true, mutable: true }) focusSetProgrammatically: boolean = false;

  /**
   * Displays the input as textarea.
   * @example <envc-eon-ui-input textarea="true"></envc-eon-ui-input>
   */
  @Prop() textarea: boolean = false;

  /**
   * Fixed with for the input field
   */
  @Prop() width: string = 'auto';

  /**
   * Set to true if the input is required.
   *
   * @example <envc-eon-ui-input required="required"></envc-eon-ui-input>
   */
  @Prop() required: boolean;

  /**
   * Set to true if the input is disabled.
   *
   * @example <envc-eon-ui-input disabled="disabled"></envc-eon-ui-input>
   */
  @Prop() disabled: boolean;

  /**
   * Sets the type of the input. See https://www.w3schools.com/html/html_form_input_types.asp for valid types.
   *
   * @example <envc-eon-ui-input type="text"></envc-eon-ui-input>
   */
  @Prop({ reflect: true, mutable: true }) type: InputType = 'text';

  /**
   * Sets the label for the input element.
   *
   * @example <envc-eon-ui-input label="First Name"></envc-eon-ui-input>
   */
  @Prop() label: string;

  /**
   * Defines whether the label should be placed above the component.
   */
  @Prop() labelOutside: boolean = false;

  /**
   * Defines whether the label should be placed before the input field.
   */
  @Prop() labelBefore: boolean = false;

  /**
   * Defines whether the label should be hidden visually, i.e. only used for screen readers
   */
  @Prop() labelVisuallyHidden: boolean = false;

  /**
   * **Internal use only! Please don't use this property directly.**
   * &nbsp;<br>
   * Sets the text of the error message.
   * @example <envc-eon-ui-input error="true" error-message="this is an error message"></envc-eon-ui-input>
   */
  @Prop({ mutable: true }) errorMessage: string;

  /**
   * An empty Placeholder for an optional custom Error Message for CustomValidation.
   * @example <envc-eon-ui-input custom-error-message="this is an custom error message"></envc-eon-ui-input>
   */
  @Prop({ mutable: true }) customErrorMessage: string;

  /**
   * A custom message to show when the required field has no value
   */
  @Prop({ mutable: true }) customRequiredMessage: string;

  /**
   * **Internal use only! Please don't use this property directly.**
   * &nbsp;<br>
   * Set to true if the Input is in error state and the error message should be displayed.
   * @example <envc-eon-ui-input invalid="true"></envc-eon-ui-input>
   */
  @Prop({ reflect: true, mutable: true }) invalid: boolean = false;

  /**
   * ** A dedicated invalid attribute to be used in more reactive scenarios like angular, where the framework controls validation rules.**
   * &nbsp;<br>
   * @example <envc-eon-ui-input ing-invalid="true"></envc-eon-ui-input>
   */
  @Prop() showError: boolean | undefined;

  /**
   * Sets the text of the success message.
   * @example <envc-eon-ui-input success="true" success-message="this is a success message"></envc-eon-ui-input>
   */
  @Prop() successMessage: string;

  /**
   * Set to true if the Input is in success state and the success message should be displayed.
   * @example <envc-eon-ui-input success="true"></envc-eon-ui-input>
   */
  @Prop() success: boolean = false;

  /**
   * Set to true if the Input is in info state and the info message should be displayed.
   * @example <envc-eon-ui-input info="true"></envc-eon-ui-input>
   */
  @Prop() info: boolean = false;

  /**
   * Sets the text of the info message.
   * @example <envc-eon-ui-input  info="true" info-message="this is an info message"></envc-eon-ui-input>
   */
  @Prop() infoMessage: string;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or envc-eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: InputSchemeName;

  /**
   * Name of the trailing icon to display.
   * @example <envc-eon-ui-input trailig-icon="true" trailing-icon-name="search"></envc-eon-ui-input>
   */
  @Prop() trailingIconName: string;

  /**
   * Label of the trailing icon
   */
  @Prop() trailingIconLabel: string;

  /**
   * Determine if the trailing icon to clear the input is shown.
   * @example <envc-eon-ui-input clear-input-icon="true"></envc-eon-ui-input>
   */
  @Prop() clearInputIcon: boolean = false;

  /**
   * Provide an icon for the messages (info, error and success).
   * If no icon is set, the default icons are used.
   * @example <envc-eon-ui-input success="true" success-message="this is a success" icon="thumb_up"></envc-eon-ui-input>
   */
  @Prop() messageIcon: string;

  /**
   * Determine if the input is in focus style
   * @example <envc-eon-ui-input focus-style="true"></envc-eon-ui-input>
   */
  @Prop() focusStyle: boolean = false;

  /**
   * Determine if the input is read only
   * @example <envc-eon-ui-input read-only="true"></envc-eon-ui-input>
   */
  @Prop() readOnly: boolean = false;

  /**
   * Determine if the input is a masked input with "date" (dd.mm.yyyy)
   * @example <envc-eon-ui-input date-mask="true"></envc-eon-ui-input>
   * @disableControlInStorybook true
   */
  @Prop() dateMask: boolean = false;

  /**
   * The format of the date used for the masked input. Use this for different separators too."
   * dd = days
   * MM = months (case sensitive!!)
   * yyyy = years
   * @example <envc-eon-ui-input date-format="dd/MM/yyyy"></envc-eon-ui-input>
   * @default "dd.MM.yyyy"
   * @disableControlInStorybook true
   */
  @Prop() dateFormat;

  /**
   * The minimum date. provided in format "dd.MM.yyyy"
   * @disableControlInStorybook true
   */
  @Prop() dateMin: string;

  /**
   * The maximum date. provided in format "dd.MM.yyyy"
   * @disableControlInStorybook true
   */
  @Prop() dateMax: string;

  /**
   * Determine if weekends are allowed inside the date-mask
   * @disableControlInStorybook true
   */
  @Prop() dateMaskAllowWeekends: boolean = true;

  /**
   * Placeholder for the input
   * @example <envc-eon-ui-input placeholder="vorname"></envc-eon-ui-input>
   */
  @Prop() placeholder: string;

  /**
   * Rows: Gives flexibility to editor for setting number of rows in Textarea field
   */
  @Prop() rows: number;

  /**
   * Cols: Gives flexibility to editor for setting number of cols in Textarea field
   */
  @Prop() cols: number;

  /**
   * Autocomplete: Defines if the input should have autocomplete function
   */
  @Prop() autocomplete: boolean = false;

  /**
   * autocompleteKeyword aka autocomplete(native input field): provides automated assistance in filling out form field values
   * as well as guidance to the browser as to the type of information expected in the field.
   * Typically values come from past values entered by the user, but they may also come from pre-configured values which can be set in autofill feature provided by browser.
   * For instance, a browser might let the user save their name, address, phone number, and email addresses for autocomplete purposes
   * This Property gives liberty to input fields to participate in Form Autofill feature of broswer
   * Source: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   * @example <envc-eon-ui-input label="First Name" autocomplete-keyword="given-name" id="fname" name="fname"></envc-eon-ui-input>
   */
  @Prop() autocompleteKeyword: string;

  /**
   * Autocomplete Terms: used to save the autocomplete API answer
   */
  @Prop() autocompleteTerms: string;

  /**
   * Autocomplete terms separator
   */
  @Prop() autocompleteSeperator: string = ',';

  /**
   * Property  to store the formatted value
   */
  @Prop({ reflect: true, mutable: true }) formattedValue: string = '';

  /**
   * Autocomplete Start: Defines after how many characters the loading indicator should be shown.
   * "3" would mean as soon as the 3rd character has been typed.
   */
  @Prop() autocompleteStart: number = 3;

  /**
   * Defines pattern mask for input.<br>
   * Supported input types - `number`, `text`, `tel`.
   *
   * Look <a href="https://imask.js.org/guide.html#masked-pattern" target="_blank">here</a> for more details.
   * @example <envc-eon-ui-input type="tel" pattern="(000)000-00-00"></envc-eon-ui-input>
   */
  @Prop() patternMask: string = '';

  // ENVIAM
  @Prop() pattern: string = '';

  /**
   * Specifies the minimum value.
   * When user enters value below `min`, value is being replaced with `min` value on blur event.
   * @example <envc-eon-ui-input min="10"></envc-eon-ui-input>
   */
  @Prop() min: string;

  /**
   * Specifies the maximum value.
   * @example <envc-eon-ui-input max="1000"></envc-eon-ui-input>
   */
  @Prop() max: string;

  /**
   * Sets thousend separator for numeric values.
   * Works only with input `type="text"`
   * @example <envc-eon-ui-input type="text" thousand-separator="."></envc-eon-ui-input>
   */
  @Prop() thousandSeparator: string = '';

  /**
   * Sets fractional delimiter for numeric values.
   * Works only with input `type="text"`
   * @example <envc-eon-ui-input type="text" radix="."></envc-eon-ui-input>
   */
  @Prop() radix: string = ',';

  /**
   * This can be activated if component is used internally in other form components like date picker.
   * It deactivates the build in validation of input component so that it is exclusively handled by parent component.
   * @disableControlInStorybook true
   */
  @Prop() encapsulated: boolean = false;

  /**
   * Currency symbol
   */
  @Prop() currency: InputCurrencies;

  /**
   * Input field will allow to enter the number of digits up to the scale value after the comma (,). 0 for integers for masked values
   */
  @Prop() scale: string = '2';

  /**
   * Sets the consumption value to identify the field for any specific purpose
   */
  @Prop() dataTargetConsumption: string = '';

  /**
   * Defines the height of the drop down option list.
   * @example "200px", "100px", "auto".
   * If not set, height sticks to the max-height which is calc(100vh - 96px)
   */
  @Prop() autocompletePopupHeight: string;

  /**
   * Specifies the label to use for accessibility.
   */
  @Prop({ attribute: 'a11y-aria-label', mutable: true }) a11yAriaLabel: string;

  /**
   * id of an element that describes the input element
   */
  @Prop({ attribute: 'a11y-describedby', mutable: true }) a11yDescribedby: string;

  /**
   * Prevents the value from being pasted.
   */
  @Prop() blockPaste: boolean = false;

  /**
   * Sets the input mode, default value is text
   */
  @Prop() internalInputMode: InputModeOptions = INPUT_MODE_OPTIONS[1];

  /**
   * Represents the necessity level for the input validation.
   * Possible values:
   * - 'none' : No validation required.
   * - 'optional' : Validation is optional.
   * - 'required' : Validation is mandatory.
   */
  @Prop() highlightedNecessity: FormFieldNecessity = FORM_FIELD_NECESSITY_NONE;

  private observeInfoIconSlot() {
    const observer = new MutationObserver(() => {
      this.detectInfoIcon();
    });

    observer.observe(this.hostElement, {
      childList: true,
      subtree: true,
    });
  }

  private detectInfoIcon() {
    const infoIcon =
      this.hostElement.querySelector('[slot="info-icon"]') || this.hostElement.querySelector('ENVC-EON-UI-INFO-ICON');

    const isNowAvailable = !!infoIcon;

    if (isNowAvailable && !this.isInfoIconAvailable) {
      this.isInfoIconAvailable = true;

      infoIcon?.addEventListener(
        'iconSrcLoaded',
        () => {
          this.delegateInputInnerOffsetVariables();
        },
        { once: true }
      );
    } else {
      this.isInfoIconAvailable = false;
    }
  }

  /**
   * Removes the value and puts the input into initial state with an empty value.
   */
  @Method()
  async purgeValue() {
    this.clearValue();
  }

  /**
   * Checks the input type and internalInputMode and based on those values sets the "finalInputMode".
   */
  @Watch('internalInputMode')
  @Watch('type')
  setInputMode() {
    if (this.internalInputMode === 'none') return (this.finalInternalInputMode = null);
    if (this.type === 'number') return (this.finalInternalInputMode = 'numeric');
    this.finalInternalInputMode = this.internalInputMode;
  }

  componentWillLoad() {
    this.deprecatedFallback();
    this.internalId = `input-${uniqueId()}`;
    this.applyScheme();
    this.setCurrentLocalization();
    this.setInternalType();
    this.setDefaultThousandSeparator();

    // If dateFormat is not set use the format coming from localization instead.
    if (!this.dateFormat && this.currentLocalization) this.dateFormat = this.currentLocalization.dateFormat;
    this.setPlaceholder();
    this.detectInfoIcon();
    this.observeInfoIconSlot();
  }

  componentDidLoad() {
    this.executeIfComponentRendered(() => {
      this.setDateMask();
      this.setPatternMask();
      this.setNumberMask();
      this.validateRequired();
      this.setCssVariables();

      if (this.value) this.handleInput(); // set initial value on internal input
      this.delegateInputInnerOffsetVariables();
      this.setInputMode();

      // ENVIAM
      this.internals.setFormValue(this.value);
      this.internals.setValidity(this.input.validity, this.input.validationMessage, this.input);
      this.validity = this.internals.validity;
      this.validationMessage = this.internals.validationMessage;
      this.defaultValue = this.value;
    });
  }

  deprecatedFallback() {
    this.a11yAriaLabel = this.hostElement.getAttribute('aria-label') ?? this.a11yAriaLabel;
    this.a11yDescribedby = this.hostElement.getAttribute('aria-described-by') ?? this.a11yDescribedby;
  }

  executeIfComponentRendered(callback: () => void) {
    if (this.componentRendered) {
      callback();
    } else {
      setTimeout(callback, 100);
    }
  }

  componentDidRender() {
    /**
     * This produces a Stencil warning regarding a possible render-loop.
     * This is intended here - we need a rerender after the inner div has been rendered to get its real height.
     * After that, the state will update and the item will rerender again but this time Stencil detects that the value didn't change anymore.
     * Therefore, this will NOT result in a render loop and the warning can be ignored safely.
     */
    this.componentRendered = true;
    this.delegateInputInnerOffsetVariables();
  }

  /**
   * Saves the given error message and assumes that the component is invalid if any error message is provided.
   * @param component The FormComponent that should handle the custom validation.
   * @param errorMessage An error message of a custom validator. If none is supplied, the custom validation is considered to be valid.
   */
  @Method()
  async handleCustomValidation(errorMessage: string) {
    await handleCustomValidationDelegate(this, errorMessage);
  }

  /**
   * Validates that the component has a value if the required attribute is set.
   */
  async validateRequired(): Promise<boolean> {
    return await validateRequiredValueDelegate(this);
  }

  /**
   * Triggers the processing
   * Sets a default error message if required validation failed or takes the custom validation error message otherwise.
   */
  @Watch("showError")
  @Method()
  async triggerValidation(): Promise<boolean> {    
    return await triggerValidationDelegate(this);
  }

  /**
   * Combines the result of the internal required validator and the custom validator.
   * Sets a default error message if required validation failed or takes the custom validation error message otherwise.
   */
  @Watch('required')
  @Method()
  async validate(): Promise<boolean> {
    return await validateDelegate(this);
  }

  /**
   * Focus the internal input.
   */
  @Method()
  async focusInput(): Promise<void> {
    this.input.focus();
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  @Watch('type')
  setInternalType() {
    // Sets the internal type to the type first time. This is only needed for hiding and showing password.
    // In this case it toggles between type password and text.
    this.internalType = this.type;
    this.setCssVariables();
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  /**
   * Update custom-required-message
   */
  @Watch('customRequiredMessage')
  setCustomRequiredMessage() {
    this.validate();
  }

  /**
   * Checks if the input has a placeholder or a mask to place the label correctly
   */
  @Watch('placeholder')
  @Watch('dateMask')
  hasPlaceholder(): boolean {
    if (this.placeholder || this.dateMask) {
      return true;
    }
  }

  /**
   * Cannot use delegate function here because we need the current background Color
   */
  @Watch('scheme')
  applyScheme() {
    applySchemeDelegate(this);
    this.setLabelBackgroundColor();
  }

  setLabelBackgroundColor() {
    const backgroundColor = getColorOfNearestBackground(this.hostElement);
    this.hostElement.style.setProperty(`--currentBackgroundColor`, `var(--${backgroundColor})`);
  }

  @Watch('activeScheme')
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  handleDateKeyDown(event: KeyboardEvent) {
    if (this.dateMask) {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.setPreviousDatePart();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.setNextDatePart();
          break;
        case 'Tab':
          if (event.shiftKey) {
            if (!this.isFirstDatePart()) {
              event.preventDefault();
              this.setPreviousDatePart();
            }
          } else if (!this.isLastDatePart()) {
            event.preventDefault();
            this.setNextDatePart();
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.changeSelectedDatePart('increase');
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.changeSelectedDatePart('decrease');
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
        case '0':
          const nextCharacter = this.input.selectionStart + 1;
          if (['.', '/', '-'].includes(this.input.value.charAt(nextCharacter))) {
            setTimeout(() => {
              this.input.setSelectionRange(nextCharacter + 1, nextCharacter + 1);
            }, 50);
            this.setNextDatePart();
          }
          break;
      }
    }
  }

  private setNextDatePart() {
    const index = this.dateFormat.toLowerCase().lastIndexOf(this.selectedDatePart) + 2;

    if (index < this.dateFormat.length) {
      this.selectedDatePart = this.dateFormat.toLowerCase().charAt(index) as SelectedDatePart;
    }
  }

  private setPreviousDatePart() {
    const index = this.dateFormat.toLowerCase().indexOf(this.selectedDatePart) - 2;

    if (index > 0) {
      this.selectedDatePart = this.dateFormat.toLowerCase().charAt(index) as SelectedDatePart;
    }
  }

  private isFirstDatePart() {
    return this.dateFormat.toLowerCase().indexOf(this.selectedDatePart) === 0;
  }

  private isLastDatePart() {
    return this.dateFormat.toLowerCase().lastIndexOf(this.selectedDatePart) > 6;
  }

  private changeSelectedDatePart(type: 'increase' | 'decrease') {
    if (!this.value) {
      this.value = format(new Date(), 'yyyy-MM-dd');
    } else {
      const dateParts = this.value.split('-');
      let datePartValue;
      switch (this.selectedDatePart) {
        case 'd':
          if (type === 'increase') {
            datePartValue = dateParts[2] !== '31' ? Number(dateParts[2]) + 1 : 1;
          } else {
            datePartValue = dateParts[2] !== '01' ? Number(dateParts[2]) - 1 : 31;
          }
          dateParts[2] = datePartValue < 10 ? `0${datePartValue}` : datePartValue.toString();
          break;
        case 'm':
          if (type === 'increase') {
            datePartValue = dateParts[1] !== '12' ? Number(dateParts[1]) + 1 : 1;
          } else {
            datePartValue = dateParts[1] !== '01' ? Number(dateParts[1]) - 1 : 12;
          }
          dateParts[1] = datePartValue < 10 ? `0${datePartValue}` : datePartValue.toString();
          break;
        case 'y':
          if (type === 'increase') {
            dateParts[0] = (Number(dateParts[0]) + 1).toString();
          } else {
            dateParts[0] = (Number(dateParts[0]) - 1).toString();
          }
          break;
      }
      this.value = dateParts.join('-');
      this.selectDatePart();
    }
  }

  handleDateFieldFocus(event?: FocusEvent) {
    if (this.dateMask) {
      event?.preventDefault();

      this.selectedDatePart = 'd';
      if (this.dateFormat.toLowerCase().charAt(0) === 'm') {
        this.selectedDatePart = 'm';
      }
      if (this.dateFormat.toLowerCase().charAt(0) === 'y') {
        this.selectedDatePart = 'y';
      }

      this.selectDatePart();
    }
  }

  @Watch('selectedDatePart')
  selectDatePart() {
    this.executeIfComponentRendered(() => {
      const position = this.dateFormat.toLowerCase().indexOf(this.selectedDatePart);
      const maskLength = this.selectedDatePart === 'y' ? 4 : 2;
      this.input.setSelectionRange(position, position + maskLength);
    });
  }

  /**
   * Main value change handler for input.
   * Differes between internal value changes (due to user input) and this.value changes set via attribute.
   */
  @Watch('value')
  handleInput(event?) {
    this.executeIfComponentRendered(() => {
      /** * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
       * value changed by user input
       * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
      if (event && event.type === 'input') {
        this.valueChangedEventEmitted = true;
        if (this.internalPatternMask) {
          this.value = this.internalPatternMask.unmaskedValue;
        } else if (this.internalNumberMask) {
          if (this.min) {
            // if min, value can only be set delayed on blur as it might be adjusted.
            // otherwise it would block the user from typing in 11 if min is 6.
            // the informatino whether the value had been updated is stored in tempValueForBlurComparison.
            this.tempValueForBlurComparison = this.internalNumberMask.unmaskedValue;
          } else {
            this.value = this.internalNumberMask.unmaskedValue;
          }
        } else if (this.internalDateMask) {
          if (this.internalDateMask.typedValue) {
            this.value = format(new Date(this.internalDateMask.typedValue), 'yyyy-MM-dd');
          } else {
            this.value = '';
            this.formattedValue = '';
          }
        } else {
          this.value = (event.target as HTMLInputElement).value;
        }

        if (this.internalNumberMask && this.min) {
          this.valueChanged.emit(this.internalNumberMask.unmaskedValue);
        } else {
          this.valueChanged.emit(this.value);
        }

        this.valueChangedEventEmitted = false;
        this.internals.setValidity(this.input.validity, this.input.validationMessage, this.input);
        this.internals.setFormValue(this.value);
        return;
      }

      /** * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
       * value changed by attribute change
       * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
      if (this.internalPatternMask) {
        this.internalPatternMask.value = this.value;
        this.input.value = this.internalPatternMask.masked.value;
        this.formattedValue = this.internalPatternMask.masked.value;
      } else if (this.internalNumberMask) {
        this.internalNumberMask.value = this.value;
        this.input.value = this.internalNumberMask.masked.value;
        this.formattedValue = this.internalNumberMask.masked.value as string;
      } else if (this.internalDateMask) {
        if (isValid(new Date(this.value))) {
          this.internalDateMask.typedValue = new Date(this.value);
          this.formattedValue = this.internalDateMask.masked.value;
        } else {
          this.input.value = this.value;
          this.formattedValue = this.value;
        }
      } else {
        if (this.input.value !== this.value) {
          this.input.value = this.value;
        }
        this.formattedValue = this.value;
      }
      !this.encapsulated && this.validate();

      if (!this.valueChangedEventEmitted) this.valueChangedProgrammatically.emit(this.value);
      this.valueChangedEventEmitted = false;
      this.internals.setValidity(this.input.validity, this.input.validationMessage, this.input);
      this.internals.setFormValue(this.value);
      // this.sendAutocompleteRequest();
    });
  }

  setPlaceholder() {
    // only done for iOS Devices as in those cases placeholder is missing
    if (this.type === 'date' && !this.placeholder && isSmartDevice()) {
      this.placeholder = this.getDeviceDateFormat();
    }
  }

  /**
   * For native date pickers a date format is chosen based on the device language.
   * This function returns the date format which is taken by the browser and translates it in the current language.
   */
  getDeviceDateFormat() {
    let dummyDate = new Date(2020, 11, 31);
    let placeholderString = dummyDate.toLocaleDateString();

    placeholderString = placeholderString.replace(
      '31',
      `${this.currentLocalization.localeDay}${this.currentLocalization.localeDay}`
    );
    placeholderString = placeholderString.replace(
      '12',
      `${this.currentLocalization.localeMonth}${this.currentLocalization.localeMonth}`
    );
    placeholderString = placeholderString.replace(
      '2020',
      `${this.currentLocalization.localeYear}${this.currentLocalization.localeYear}${this.currentLocalization.localeYear}${this.currentLocalization.localeYear}`
    );

    return placeholderString;
  }

  /**
   * Show or hide the autocomplete dropdown field depending on terms coming from the API
   */
  @Watch('autocompleteTerms')
  handleAutocomplete() {
    let tempTerms;
    this.preloader = false;
    this.collapsed = false;

    if (this.autocompleteTerms.length > 0) {
      tempTerms = this.autocompleteTerms.split(this.autocompleteSeperator);
      this.results = tempTerms.map((term: string) => term.trim());
    } else {
      this.results = [];
    }
  }

  /**
   * Remove preloader if there is no response from server
   */
  @Watch('invalid')
  removePreloader() {
    if (this.invalid) this.preloader = false;
  }

  @Watch('dateFormat')
  setDateMask(dateFormatString?: string) {
    this.executeIfComponentRendered(() => {
      if (this.internalDateMask) {
        // Reset mask on date format change
        this.input.value = '';
        this.internalDateMask.destroy();
      }
      if (!dateFormatString) {
        dateFormatString = this.dateFormat;
      }

      if (this.dateMask) {
        this.masked = true;

        //set the input to a date masked input
        this.internalDateMask = IMask(this.input, {
          mask: Date,
          lazy: false,
          overwrite: true,
          pattern: dateFormatString,
          blocks: {
            dd: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 31,
              maxLength: 2,
              placeholderChar: this.currentLocalization.localeDay,
            },
            MM: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 12,
              maxLength: 2,
              placeholderChar: this.currentLocalization.localeMonth,
            },
            yyyy: {
              mask: IMask.MaskedRange,
              from: 1900,
              to: 9999,
              maxLength: 4,
              placeholderChar: this.currentLocalization.localeYear,
            },
          },
          format: (date) => {
            if (date) {
              return format(date, dateFormatString);
            }
          },
          parse: (str) => {
            if (str) {
              const date = parse(str, dateFormatString, new Date());
              return date;
            }
          },
        });
      }
    });
  }

  @Watch('patternMask')
  setPatternMask() {
    this.executeIfComponentRendered(() => {
      const isSupportedType = this.type === 'number' || this.type === 'text' || this.type === 'tel';

      if (this.internalPatternMask) {
        this.internalPatternMask = null;
      }

      if (isSupportedType && this.patternMask) {
        this.masked = true;
        this.internalPatternMask = IMask(this.input, {
          mask: this.patternMask,
        });
      }
    });
  }

  @Watch('dataTargetConsumption')
  setDefaultThousandSeparator() {
    if (this.currentLocalization && this.dataTargetConsumption) {
      this.thousandSeparator = this.currentLocalization.thousandsSeparator;
    }
  }

  @Watch('min')
  @Watch('max')
  @Watch('thousandSeparator')
  @Watch('scale')
  @Watch('radix')
  setNumberMask() {
    this.executeIfComponentRendered(() => {
      if (this.min || this.max || this.thousandSeparator || this.currency) {
        this.masked = true;
        let maskOptions = {};

        if (this.currency) {
          const currencyInfo = INPUT_CURRENCIES.find((obj) => {
            return obj.name === this.currency;
          });

          this.placeholder = currencyInfo.currencySymbol;

          maskOptions = {
            mask: [
              { mask: '' },
              {
                mask: currencyInfo.currencyFormat,
                lazy: false,
                blocks: {
                  num: {
                    mask: Number,
                    scale: this.scale,
                    normalizeZeros: false,
                    thousandsSeparator: currencyInfo.thousandSeparator,
                    radix: currencyInfo.currencyRadix,
                    mapToRadix: ['.', ','],
                  },
                },
              },
            ],
          };
        } else {
          maskOptions = {
            mask: Number,
            thousandsSeparator: this.thousandSeparator || '',
            min: parseInt(this.min) || null,
            max: parseInt(this.max) || null,
            mapToRadix: parseInt(this.scale) ? ['.', ','] : [],
            radix: parseInt(this.scale) ? this.radix : 'null',
            scale: this.scale,
          };
        }

        if (this.internalNumberMask) {
          // @ts-ignore
          this.internalNumberMask.updateOptions(maskOptions);
        } else {
          // @ts-ignore
          this.internalPatternMask = IMask(this.input, maskOptions);
        }
      }
    });
  }

  @Watch('masked')
  toggleMaskedInputConfig() {
    if (this.masked) {
      this.type = 'text';
    }
  }

  sendAutocompleteRequest() {
    if (this.autocomplete && this.value.length >= this.autocompleteStart) {
      this.preloader = true;
    }

    // here should be a request to API to update terms property
  }

  /**
   * Clears the value of the input
   * @param _event the event of the input
   */
  private clearValue(event?) {
    event?.preventDefault();
    this.value = '';
    this.formattedValue = '';

    if (this.internalDateMask) {
      this.internalDateMask.value = '';
      this.input.focus();
      this.handleDateFieldFocus();
    }
  }

  /**
   * toggles the value of the input field between 'password' and 'text'
   * @param _event the event of the click
   */
  private togglePasswordVisible(_event) {
    this.internalType = this.passwordVisible ? INPUT_PASSWORD : INPUT_TEXT;
    this.passwordVisible = !this.passwordVisible;
    this.focusSetProgrammatically = true;
    this.input.focus();
  }

  private onFocus(event: FocusEvent) {
    if(this.openAutocompleteOnFocus && !this.focusSetProgrammatically) {
      this.collapsed = this.autocomplete && this.value.length < this.autocompleteStart;
      this.hideAutocomplete = this.collapsed;
    }
    this.inputFocus.emit(event);
    this.focusSetProgrammatically = false;
    this.isFocus = true;
    (event.currentTarget as HTMLElement).focus();
  }

  private onBlur(event: FocusEvent) {
    this.inputBlur.emit(event);
    this.isFocus = false;

    // if min, value can only be set delayed on blur as it might be adjusted
    if (this.min) {
      this.value = this.internalNumberMask.unmaskedValue;
      if (this.tempValueForBlurComparison && this.tempValueForBlurComparison !== this.value) {
        this.valueChangedProgrammatically.emit(this.internalNumberMask.unmaskedValue);
      }
    }
  }

  private onPaste(event: ClipboardEvent) {
    if (this.blockPaste) {
      event.preventDefault();
    }
  }

  /**
   * Get attributes for textarea and input so we do not need to declare them twice
   */
  private getProps() {
    let describedBy = this.a11yDescribedby;
    if (this.invalid) {
      describedBy = `${this.internalId}-invalid`;
    } else if (this.success) {
      describedBy = `${this.internalId}-success`;
    } else if (this.info) {
      describedBy = `${this.internalId}-info`;
    }

    const props = {
      class: pfx({
        'eon-input': true,
        touched: this.value,
        error: this.invalid,
        success: this.success,
        focus: this.focusStyle,
        placeholder: this.hasPlaceholder(),
        'date-field': this.internalDateMask,
      }),
      onInput: (event: InputEvent) => this.handleInput(event),
      onKeyDown: (event: KeyboardEvent) => this.handleDateKeyDown(event),
      onFocus: (event: FocusEvent) => {
        this.onFocus(event);
        this.selectedDatePart === '' ? this.handleDateFieldFocus(event) : this.selectDatePart();
      },
      onClick: (event: FocusEvent) => this.handleDateFieldFocus(event),
      required: this.required,
      disabled: this.disabled || this.readOnly,
      // ENVIAM
      pattern: !!this.pattern ? this.pattern : undefined,
      id: this.internalId,
      readonly: this.readOnly,
      ...(this.a11yAriaLabel
        ? { 'aria-label': `${this.a11yAriaLabel} ${renderRelevantNecessity(this, 'standalone')}`.trim() }
        : {}),
      'aria-describedby': describedBy,
      autocomplete: this.autocompleteKeyword, // browser autocomplete feature
    };

    return props;
  }

  /**
   * Set the input value on click on autocomplete results
   */
  private onOptionClick(value) {
    this.value = value;
    this.preloader = false;
    this.autocompleteSelected.emit(value);
    this.results = [];
    this.hideAutocomplete = true;
  }

  private handleKeyDown(e: KeyboardEvent, value) {
    if (this.disabled) {
      return;
    }

    const elm = e.currentTarget as HTMLElement;

    switch (e.key) {
      case KeyIds.SPACE:
      case KeyIds.ENTER:
        e.preventDefault();

        this.value = value;
        this.results = [];
        this.preloader = false;
        this.autocompleteSelected.emit(value);
        this.hideAutocomplete = true;
        this.focusSetProgrammatically = true;
        this.input.focus();
        // this.autocompleteSelected.emit(value);

        break;

      case KeyIds.DOWN:
        e.preventDefault();
        e.stopPropagation();

        const nextElm = elm.nextElementSibling as HTMLElement;

        if (nextElm) {
          nextElm.focus();
        }

        break;

      case KeyIds.UP:
        e.preventDefault();

        const prevElm = elm.previousElementSibling as HTMLElement;

        if (prevElm) {
          prevElm.focus();
        }

        break;
    }
  }

  private handleInputKeyDown(e: KeyboardEvent) {
    if (this.disabled) {
      return;
    }
    if (this.autocomplete) {
      if (e.key !== KeyIds.ENTER && e.key !== KeyIds.SPACE && e.key !== KeyIds.TAB) {
        this.hideAutocomplete = false;
      }
      switch (e.key) {
        case KeyIds.DOWN:
          e.preventDefault();
          let option = this.hostElement?.shadowRoot?.querySelector('.eonui-input-autocomplete-option') as HTMLElement;
          option?.focus();

          break;

        case KeyIds.ESCAPE:
          e.preventDefault();
          this.closeAutocompleteDropdown(e);

          break;
      }
    }
    // for firefox and Safari:: this logic is implemented to handle the numberic characters
    // firefox ticket : https://bugzil.la/1398528
    if (isFirefox() || isSafari()) {
      if (this.type === 'number') {
        if (!e.key.match(NUMBER_REGEX1)) {
          e.preventDefault();
        }
      }
    }
  }

  private handlePasteInput(e: ClipboardEvent) {
    if (isFirefox() || isSafari()) {
      const pasteText = e.clipboardData.getData('text');
      const isNum = !pasteText.match(NUMBER_REGEX2);
      if (this.type === 'number' && !isNum) {
        e.preventDefault();
      }
    }
  }

  private closeAutocompleteDropdown(e) {
    const srcElement = e.srcElement as HTMLElement;
    if (srcElement?.tagName != 'ENVC-EON-UI-INPUT' && !this.hostElement.contains(srcElement)) {
      this.collapsed = true;
    }
  }

  @Listen('valueChanged')
  @Listen('valueChangedProgrammatically')
  handleValueChanged() {
    this.collapsed = this.value?.length < this.autocompleteStart;
  }

  @Listen('click', { target: 'document' })
  onDocumentClick(event) {
    this.closeAutocompleteDropdown(event);
  }

  private delegateInputInnerOffsetVariables() {
    const labelOffset = this.navigationElementsContainer?.offsetWidth;

    if (labelOffset) setCssVariable(this.hostElement as HTMLElement, '--inputInnerOffset', `${labelOffset}px`);
  }

  // ENVIAM
  @Method()
  async setInternalPatternMask(maskOptions: IMask.MaskedPatternOptions) {
    this.executeIfComponentRendered(() => {
      const isSupportedType = this.type === 'number' || this.type === 'text' || this.type === 'tel';

      if (this.internalPatternMask) {
        this.internalPatternMask = null;
      }

      if (isSupportedType && maskOptions) {
        this.masked = true;
        this.internalPatternMask = IMask(this.input, maskOptions);
      }
    });
  }

  defaultValue: string;

  formDisabledCallback(disabled: boolean) {
    if (this.disabled !== disabled) {
      this.disabled = disabled;
    }
  }

  formResetCallback() {
    this.internals.setValidity({});
    this.internals.setFormValue(this.defaultValue);
  }

  formStateRestoreCallback(state, _mode) {
    this.value = state;
  }

  @Method()
  async checkValidity() {
    return this.internals.checkValidity();
  }

  @Method()
  async reportValidity() {
    return this.internals.reportValidity();
  }

  @Method()
  async setCustomValidity(errorMessage: string) {
    this.errorMessage = errorMessage;
    await this.handleCustomValidation(errorMessage);
  }

  @Prop()
  validity: ValidityState;

  @Prop()
  validationMessage: string;

  render() {
    let input;
    if (this.textarea) {
      input = (
        <textarea
          ref={(el) => (this.input = el as HTMLTextAreaElement)}
          onBlur={(e) => this.onBlur(e)}
          onPaste={(e) => this.onPaste(e)}
          placeholder={this.placeholder && `${this.placeholder} ${renderRelevantNecessity(this, 'standalone')}`.trim()}
          rows={this.rows}
          cols={this.cols}
          name={this.name}
          value={this.value}
          {...this.getProps()}
        ></textarea>
      );
    } else {
      input = (
        <input
          type={this.internalType}
          ref={(el) => (this.input = el as HTMLInputElement)}
          onBlur={(e) => this.onBlur(e)}
          onPaste={(e) => this.onPaste(e)}
          placeholder={this.placeholder && `${this.placeholder} ${renderRelevantNecessity(this, 'standalone')}`.trim()}
          title={this.internalType == 'password' ? '' : this.formattedValue}
          name={this.name}
          min={this.dateMin && this.dateMin}
          max={this.dateMax && this.dateMax}
          inputmode={this.finalInternalInputMode}
          {...(this.width !== 'auto' ? { style: { width: this.width } } : {})}
          {...this.getProps()}
        ></input>
      );
    }

    const autocompleteVisible =
      this.autocomplete &&
      !this.hideAutocomplete &&
      !this.textarea &&
      this.results &&
      this.results.length !== 0 &&
      this.value.length >= this.autocompleteStart;

    return (
      <Host
        class={pfx({
          focus: this.isFocus,
          'label-before': this.labelBefore,
          success: this.success,
          error: this.invalid,
        })}
      >
        {(this.labelOutside || this.labelVisuallyHidden || this.labelBefore) && (
          // eslint-disable-next-line jsx-a11y/label-has-associated-control
          <label
            htmlFor={this.internalId}
            innerHTML={sanitizeHtml(
              this.label ? this.label : '' + renderRelevantNecessity(this, 'wrapped'),
              allowClassesAndLinks
            )}
            class={pfx({
              outside: this.labelOutside,
              before: this.labelBefore,
              [this.size]: true,
              error: this.invalid,
              'sr-only': this.labelVisuallyHidden,
              disabled: this.disabled,
            })}
          ></label>
        )}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          class={pfx({
            'input-wrapper': true,
            [this.size]: this.size,
            'input-textarea': this.textarea,
            'has-popup': this.autocomplete,
            'popup-visible': !this.collapsed && autocompleteVisible,
          })}
          onKeyDown={(event: KeyboardEvent) => this.handleInputKeyDown(event)}
          onPaste={(event: ClipboardEvent) => this.handlePasteInput(event)}
        >
          {input}
          {!this.labelOutside && !this.labelVisuallyHidden && !this.labelBefore && (
            // eslint-disable-next-line jsx-a11y/label-has-associated-control
            <label
              class={pfx({
                inside: !this.labelOutside,
                focussed: this.value || this.isFocus && !this.value,
              })}
              htmlFor={this.internalId}
              innerHTML={sanitizeHtml(
                `<span class="label-text">${this.label ? this.label : ''}</span>` +
                  renderRelevantNecessity(this, 'wrapped'),
                allowClassesAndLinks
              )}
            ></label>
          )}
          {!this.textarea && (
            <div
              class={pfx({
                'navigation-elements': true,
                visible: isTouchDevice() && this.type === 'date',
              })}
              ref={(el) => (this.navigationElementsContainer = el as HTMLElement)}
            >
              {this.clearInputIcon && !this.disabled && (
                <button
                  tabindex="0"
                  aria-label={this.currentLocalization.clear}
                  onClick={(e) => this.clearValue(e)}
                  class={pfx('interaction-icon clear')}
                >
                  <envc-eon-ui-icon
                    class={pfx('icon')}
                    name="close"
                    size={this.size === 'small' ? 'small' : 'normal'}
                  ></envc-eon-ui-icon>
                </button>
              )}

              {this.type === INPUT_PASSWORD && (
                <button
                  tabindex="0"
                  class={pfx('interaction-icon password-toggle')}
                  aria-label={
                    this.passwordVisible ? this.currentLocalization.hidePassword : this.currentLocalization.showPassword
                  }
                  onClick={(e) => this.togglePasswordVisible(e)}
                >
                  <envc-eon-ui-icon
                    class={pfx({
                      icon: true,
                      'interaction-icon': true,
                    })}
                    name={this.passwordVisible ? 'password_invisible' : 'password_visible'}
                  ></envc-eon-ui-icon>
                </button>
              )}

              {this.preloader && !this.textarea && (
                <div
                  class={pfx('input-autocomplete-preloader-wrapper')}
                  /* ref={(el) => (this.autoIconBox = el as HTMLElement)} */
                >
                  <envc-eon-ui-preloader-circle size={PRELOADER_CIRCLE_SIZE_SMALL}></envc-eon-ui-preloader-circle>
                </div>
              )}
              {!this.textarea && this.isInfoIconAvailable && (
                <div class={pfx('input-info-icon')}>
                  <slot name="info-icon" />
                  <slot />
                </div>
              )}
              {this.trailingIconName && !this.textarea && (
                // ENVIAM icon-background query
                <div
                  class={pfx({
                    'icon-background': this.trailingIconName !== 'calendar',
                    'icon-focussable': !!this.iconClick,
                  })}
                >
                  <button tabindex="0" class={pfx('icon-button')} onClick={(e) => this.iconClick.emit(e)} disabled={this.disabled}>
                    <envc-eon-ui-icon
                      aria-label={this.trailingIconLabel || this.trailingIconName}
                      class={pfx('icon')}
                      name={this.trailingIconName}
                      size={this.size === 'small' ? 'small' : 'normal'}
                      noSpace={true}
                    ></envc-eon-ui-icon>
                  </button>
                </div>
              )}
            </div>
          )}
          {autocompleteVisible && (
            <div class={pfx('input-autocomplete-wrapper')} style={{ maxHeight: this.autocompletePopupHeight }}>
              {this.results.map((item, i) => (
                <div
                  tabIndex={0}
                  role={'button'}
                  class={pfx('input-autocomplete-option')}
                  key={item + i}
                  onClick={() => this.onOptionClick(item)}
                  onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event, item)}
                  innerHTML={sanitizeHtml(item.split(this.value).join(`<b>${this.value}</b>`))}
                ></div>
              ))}
            </div>
          )}
        </div>

        {this.success && (
          <envc-eon-ui-form-message
            id={`${this.internalId}-success`}
            type="success"
            class={pfx('success-message')}
            icon={this.messageIcon}
            message={this.successMessage}
          ></envc-eon-ui-form-message>
        )}
        {this.invalid && (
          <envc-eon-ui-form-message
            id={`${this.internalId}-invalid`}
            class={pfx('error-message')}
            type="error"
            icon={this.messageIcon}
            message={this.errorMessage}
          ></envc-eon-ui-form-message>
        )}
        {this.info && (
          <envc-eon-ui-form-message
            id={`${this.internalId}-info`}
            class={pfx('info')}
            type="info"
            icon={this.messageIcon}
            message={this.infoMessage}
          ></envc-eon-ui-form-message>
        )}
      </Host>
    );
  }
}
